import { sluggify } from './misc'

const BLOCKING_REASONS = [
  'c1_not_versement_attestation',
  'c3_siret_mismatch',
  'c4_contribution_mismatch',
  'c5_wrong_organism',
  'human_rejected',
  'blurry_document',
  'incomplete_document',
  'unreadable_document',
  'invalid_document',
]

export type AttestationUrssafAi = {
  ai_validation_status: string | null
  rejection_reason: string | null
  rejection_reasons: string[]
} | null

export const attestationBlockingReason = (verdict: AttestationUrssafAi): string | null => {
  if (!verdict || verdict.ai_validation_status !== 'rejected') return null
  const reasons = verdict.rejection_reasons ?? []
  if (!reasons.some((reason) => BLOCKING_REASONS.includes(reason))) return null
  return verdict.rejection_reason?.trim() || 'Cette attestation a été refusée.'
}

export const ATTESTATION_POLL_DEADLINE_MS = 80_000

export const attestationPollDelayMs = (elapsedMs: number): number => {
  if (elapsedMs < 12_000) return 2_000
  if (elapsedMs < 30_000) return 4_000
  return 8_000
}

export const uniqueAttestationFileName = (fileName: string, stamp: number = Date.now()): string => {
  const sluggified = sluggify(fileName)
  const dot = sluggified.lastIndexOf('.')
  if (dot <= 0) return `${sluggified}-${stamp}`
  return `${sluggified.slice(0, dot)}-${stamp}${sluggified.slice(dot)}`
}

export const sameAttestationUrl = (left?: string | null, right?: string | null): boolean => {
  const normalize = (value?: string | null) => {
    if (!value) return ''
    const trimmed = value.trim()
    try {
      const url = new URL(trimmed)
      return decodeURIComponent(`${url.origin}${url.pathname}`).replace(/\/$/, '')
    } catch {
      try {
        return decodeURIComponent(trimmed).replace(/\/$/, '')
      } catch {
        return trimmed.replace(/\/$/, '')
      }
    }
  }
  return normalize(left) === normalize(right)
}

export const isCompleteSiret = (siret?: string | null): boolean => /^[0-9]{14}$/.test(siret ?? '')

/** True when an attestation URL + complete SIRET are ready and differ from the last analysed pair. */
export const shouldStartAttestationAnalysis = (params: {
  fileUrl?: string | null
  siret?: string | null
  lastAnalysedUrl?: string | null
  lastAnalysedSiret?: string | null
}): boolean => {
  const { fileUrl, siret, lastAnalysedUrl, lastAnalysedSiret } = params
  if (!fileUrl || !isCompleteSiret(siret)) return false
  if (sameAttestationUrl(fileUrl, lastAnalysedUrl) && siret === lastAnalysedSiret) return false
  return true
}

type RegistrationSnapshot = {
  attestation_urssaf?: string | null
  attestation_urssaf_ai?: AttestationUrssafAi
}

export const pollAttestationVerdict = async (
  fileUrl: string,
  read: () => Promise<RegistrationSnapshot>,
  deps: {
    sleep?: (ms: number) => Promise<void>
    now?: () => number
    deadlineMs?: number
  } = {},
): Promise<AttestationUrssafAi> => {
  const sleep = deps.sleep ?? ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)))
  const now = deps.now ?? Date.now
  const deadlineMs = deps.deadlineMs ?? ATTESTATION_POLL_DEADLINE_MS
  const started = now()
  let attempt = 0
  while (now() - started <= deadlineMs) {
    if (attempt > 0) {
      const delay = attestationPollDelayMs(now() - started)
      if (now() - started + delay > deadlineMs) break
      await sleep(delay)
    }
    let snapshot: RegistrationSnapshot
    try {
      snapshot = await read()
    } catch {
      attempt += 1
      continue
    }
    if (snapshot.attestation_urssaf && !sameAttestationUrl(snapshot.attestation_urssaf, fileUrl)) return null
    const verdict = snapshot.attestation_urssaf_ai ?? null
    if (verdict?.ai_validation_status && verdict.ai_validation_status !== 'pending') return verdict
    attempt += 1
  }
  return null
}

